// The renderers for ONE template, loaded on demand — for the public
// website.
//
// A public page used to import every template's renderers, so a site
// drawn by one of them downloaded all three: 317 scripts and
// stylesheets, two thirds of them for designs the site does not use.
// Now the page loads its own template before it renders: app.js and
// ssr.js await loadSiteComponents() for the page's theme, and the
// server names the same module in the page's <head>
// (SiteTemplateAssets), so its stylesheet is there before the first
// paint rather than arriving after the JavaScript asks for it.
//
// Each template's registry is complete on its own — sitetemplates_verify
// fails on a type one of them has not drawn — so, unlike the builder's
// templateRegistry.js, nothing here merges Classic in underneath.
//
// Keep LOADERS in step with SiteTemplateAssets::MODULES;
// sitetemplateload_verify compares them.

import { defineAsyncComponent } from 'vue';
import { templateFor } from './templates';

const LOADERS = {
    classic: () => import('./blocks.js').then((m) => m.COMPONENTS),
    trailhead: () => import('./Trailhead/index.js').then((m) => m.TRAILHEAD_COMPONENTS),
    backyard: () => import('./Backyard/index.js').then((m) => m.BACKYARD_COMPONENTS),
    hub: () => import('./Hub/index.js').then((m) => m.HUB_COMPONENTS),
    groundwork: () => import('./Groundwork/index.js').then((m) => m.GROUNDWORK_COMPONENTS),
    ridgeline: () => import('./Ridgeline/index.js').then((m) => m.RIDGELINE_COMPONENTS),
};

const loaded = {};
const pending = {};
const deferred = {};

/** Load a theme's renderers; resolves to its registry. */
export function loadSiteComponents(theme) {
    const key = templateFor(theme).key;
    pending[key] ??= LOADERS[key]().then((registry) => (loaded[key] = registry));
    return pending[key];
}

/**
 * The registry for a theme, synchronously. Loaded before the first
 * render, so this is the ordinary answer. The one way to reach a page
 * whose template is NOT loaded is a navigation inside a preview into
 * another template, and there every block waits for its renderer
 * rather than the page rendering nothing.
 */
export function siteComponentsFor(theme) {
    const key = templateFor(theme).key;
    if (loaded[key]) return loaded[key];
    if (deferred[key]) return deferred[key];
    const types = Object.keys(Object.values(loaded)[0] || {});
    deferred[key] = Object.fromEntries(types.map((type) => [type, defineAsyncComponent(
        () => loadSiteComponents({ template: key }).then((registry) => registry[type]),
    )]));

    return deferred[key];
}
